import axios, { type AxiosInstance, isAxiosError } from 'axios';
export const API_ENDPOINT = process.env.API_URL || '';

function getAxiosLibrary(): typeof axios {
  // Check if axios has a default property (ESM interop)
  if ('default' in axios && typeof axios.default === 'function') {
    return axios.default as typeof axios;
  }
  // Otherwise use axios directly
  return axios;
}

const axiosLibrary = getAxiosLibrary();

export const apiClient: AxiosInstance = axiosLibrary.create({
  baseURL: `${API_ENDPOINT}`,
  timeout: 30000,
  headers: { 'Content-type': 'application/json' },
  paramsSerializer: (params) => {
    const filteredParams = Object.fromEntries(
      Object.entries(params).filter(([_, value]) => value != null),
    );
    return new URLSearchParams(filteredParams)
      .toString()
      .replace(/\w+=&/g, '')
      .replace(/&\w+=$/, '');
  },
});

// Token refresh state - ensures only one refresh call at a time
let refreshPromise: Promise<void> | null = null;

const refreshTokenUrl = '/api/base/auth/refresh-token';

// Refreshes the auth session cookies. Exported so callers that authenticate
// outside axios (e.g. the Pleri WebSocket) can force a fresh access token.
export async function refreshSession(): Promise<void> {
  if (refreshPromise) {
    return refreshPromise;
  }

  const tenantId = localStorage.getItem('defaultTenant');
  refreshPromise = apiClient
    // Include the active tenant ID in the refresh token request if available
    // this ensures that when the access token is expired, the backend knows
    // the active tenant and generate the access token for the correct tenant.
    // without this, it would fall back to the default tenant,
    // causing tenant mismatches and incorrect data in the UI
    .post(refreshTokenUrl, tenantId ? { tenantId } : {})
    .then(() => {})
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}

/**
 * The provider sign-in endpoints are called before a session exists, so a 401
 * there must not trigger a refresh. Matched precisely rather than by the
 * `/api/base/oauth` prefix, which the authorization-server routes share and
 * which do need the refresh.
 */
const skipTokenRefreshPatterns = [
  /\/api\/base\/oauth\/[^/]+\/(init|callback|sign-up)\b/,
];

export const isSkipTokenRefreshRequest = (url: string) => {
  return (
    [
      refreshTokenUrl,
      '/api/base/auth/login',
      '/api/base/auth/mfa-verify',
      '/api/base/auth/change-password',
      '/api/base/auth/reset-password',
      '/api/base/users/mfa',
      '/api/base/auth/sso/login',
      '/api/base/auth/saml/sp-init',
      '/api/base/auth/forgot-password',
      '/api/base/auth/lookup',
      '/api/base/notifications/unsubscribe',
    ].some((x) => url.includes(x)) ||
    skipTokenRefreshPatterns.some((pattern) => pattern.test(url))
  );
};

const isSkipSignInRedirect = (url: string) => {
  return [
    '/forgot-password',
    '/reset-password',
    '/sign-up',
    '/register',
    '/unsubscribe',
    '/sign-in',
    '/sso',
  ].some((x) => url.includes(x));
};

// Response interceptor to handle 401 and refresh token
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isSkipTokenRefreshRequest(originalRequest?.url || '')
    ) {
      originalRequest._retry = true;

      try {
        await refreshSession();
      } catch (error) {
        if (
          isAxiosError(error) &&
          error.response?.status === 401 &&
          !isSkipSignInRedirect(window.location.pathname)
        ) {
          window.location.replace('/sign-in');
        }
        throw error;
      }

      return apiClient(originalRequest);
    }
    throw error;
  },
);
